import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';

type NoticeTone = 'success' | 'error';

type Notice = {
    id: number;
    tone: NoticeTone;
    title: string;
    description?: string;
    isClosing?: boolean; // Añadimos una bandera para saber cuándo está saliendo
};

type NotificationsContextValue = {
    showSuccess: (title: string, description?: string, duration?: number) => void;
    showError: (title: string, description?: string, duration?: number) => void;
};

const NotificationsContext = React.createContext<NotificationsContextValue | null>(null);

const portalRoot = document.createElement('div');
portalRoot.setAttribute('id', 'notifications-portal');
document.body.appendChild(portalRoot);

export function NotificationsProvider(props: { children: React.ReactNode }) {
    const { t } = useTranslation();
    const [notices, setNotices] = React.useState<Notice[]>([]);

    // 1. Elimina del DOM completamente
    const removeNotice = React.useCallback((id: number) => {
        setNotices((current) => current.filter((notice) => notice.id !== id));
    }, []);

    // 2. Inicia la animación de salida y luego la elimina del DOM
    const closeNotice = React.useCallback((id: number) => {
        setNotices((current) =>
            current.map((notice) =>
                notice.id === id ? { ...notice, isClosing: true } : notice
            )
        );

        // Esperamos a que termine la animación (300ms) antes de borrar
        setTimeout(() => {
            removeNotice(id);
        }, 300);
    }, [removeNotice]);

    const pushNotice = React.useCallback((tone: NoticeTone, title: string, description?: string, duration?: number) => {
        // Close all existing notices before pushing a new one
        setNotices((currentNotices) => {
            currentNotices.forEach(notice => closeNotice(notice.id));
            return []; // Clear existing notices immediately
        });

        const id = Date.now() + Math.floor(Math.random() * 1000);
        setNotices((current) => [...current, { id, tone, title, description, isClosing: false }]);

        // Usamos la duración personalizada, o la que había por defecto
        const timeoutDuration = duration ?? (tone === 'error' ? 8000 : 6000);

        window.setTimeout(() => {
            // En vez de removeNotice, llamamos a closeNotice para que anime
            closeNotice(id);
        }, timeoutDuration);
    }, [closeNotice]);

    const value = React.useMemo<NotificationsContextValue>(() => ({
        showSuccess: (title, description, duration) => pushNotice('success', title, description, duration),
        showError: (title, description, duration) => pushNotice('error', title, description, duration),
    }), [pushNotice]);

    const notificationsMarkup = (
        <>
            <style>
                {`
                /* Animación de entrada */
                @keyframes slideInRight {
                    from { transform: translateX(100%); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
                
                /* Animación de salida (NUEVA) */
                @keyframes slideOutRight {
                    from { transform: translateX(0); opacity: 1; }
                    to { transform: translateX(100%); opacity: 0; }
                }

                .notification-card {
                    animation: slideInRight 0.4s cubic-bezier(0.25, 0.8, 0.25, 1) forwards;
                    transition: all 0.3s ease;
                }
                
                /* Clase que se aplica al salir (NUEVA) */
                .notification-card.closing {
                    animation: slideOutRight 0.3s cubic-bezier(0.25, 0.8, 0.25, 1) forwards;
                }

                .notification-close-btn:hover {
                    opacity: 1 !important;
                    background: rgba(0,0,0,0.05);
                }
                `}
            </style>
            <div style={viewportStyle}>
                {notices.map((notice) => (
                    <div
                        key={notice.id}
                        style={getNoticeStyle(notice.tone)}
                        // Aplicamos la clase 'closing' dinámicamente
                        className={`notification-card ${notice.isClosing ? 'closing' : ''}`}
                    >
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                            <div style={{ flexShrink: 0, marginTop: '2px' }}>
                                {notice.tone === 'success' ? <SuccessIcon /> : <ErrorIcon />}
                            </div>
                            <div style={{ flexGrow: 1 }}>
                                <div style={noticeTitleStyle(notice.tone)}>{notice.title}</div>
                                {notice.description ? <div style={noticeDescriptionStyle}>{notice.description}</div> : null}
                            </div>
                            <button
                                onClick={() => closeNotice(notice.id)} // Usamos closeNotice al hacer click
                                style={closeButtonStyle(notice.tone)}
                                className="notification-close-btn"
                                aria-label={t('notifications.close')}
                            >
                                <CloseIcon />
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </>
    );

    return (
        <NotificationsContext.Provider value={value}>
            {props.children}
            {ReactDOM.createPortal(notificationsMarkup, portalRoot)}
        </NotificationsContext.Provider>
    );
}

export function useNotifications() {
    const context = React.useContext(NotificationsContext);

    if (!context) {
        throw new Error('useNotifications must be used within NotificationsProvider');
    }

    return context;
}

const viewportStyle: React.CSSProperties = {
    position: 'fixed',
    top: 30,
    right: 30,
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    pointerEvents: 'none',
    width: '450px',
    maxWidth: '90vw',
};

function getNoticeStyle(tone: NoticeTone): React.CSSProperties {
    return {
        pointerEvents: 'auto',
        borderRadius: '12px',
        padding: '20px',
        background: tone === 'success' ? '#f0fdf4' : '#fef2f2',
        border: `1px solid ${tone === 'success' ? '#bbf7d0' : '#fecaca'}`,
        borderLeft: `6px solid ${tone === 'success' ? '#22c55e' : '#ef4444'}`,
        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
        color: '#0f172a',
        position: 'relative',
        overflow: 'hidden',
    };
}

function noticeTitleStyle(tone: NoticeTone): React.CSSProperties {
    return {
        fontSize: '1.05rem',
        fontWeight: 600,
        lineHeight: 1.4,
        color: tone === 'success' ? '#166534' : '#991b1b',
    };
}

const noticeDescriptionStyle: React.CSSProperties = {
    marginTop: '6px',
    fontSize: '0.95rem',
    lineHeight: 1.5,
    color: '#3f3f46',
    whiteSpace: 'pre-wrap',
};

function closeButtonStyle(tone: NoticeTone): React.CSSProperties {
    return {
        background: 'transparent',
        border: 'none',
        padding: '4px',
        cursor: 'pointer',
        color: tone === 'success' ? '#166534' : '#991b1b',
        opacity: 0.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '4px',
        transition: 'all 0.2s ease',
        marginLeft: '4px',
    };
}

function SuccessIcon() {
    return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22Z" fill="#22c55e" fillOpacity="0.2"/>
            <path d="M12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22Z" stroke="#22c55e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M7.75 12L10.58 14.83L16.25 9.17004" stroke="#22c55e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    );
}

function ErrorIcon() {
    return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22Z" fill="#ef4444" fillOpacity="0.2"/>
            <path d="M12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22Z" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 8V13" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M11.9941 16H12.0031" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    );
}

function CloseIcon() {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M6 6L18 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    );
}